/**
 * ApprovalsContext - Data store for page approval data
 *
 * This provider follows the pattern established in AnnotationsProvider and
 * documented in patterns/component-architecture.md:
 * - Separate read and write contexts to prevent unnecessary re-renders
 * - Server data managed by Apollo GraphQL (useQueryInterop)
 * - UI state managed by React Context
 *
 * Usage:
 *   const { approvalRequest, loading } = useApprovals();
 *   const { startApproval, updateApprovalStatus } = useApprovalsDispatch();
 */

import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	type FC,
	type ReactNode,
} from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { usePageContentId } from '@confluence/page-context';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';

import type {
	ApprovalsContextCreateWorkflowApplicationMutation$data,
	ApprovalsContextCreateWorkflowApplicationMutation$variables,
} from './__generated__/ApprovalsContextCreateWorkflowApplicationMutation.graphql.ts';
import type {
	ApprovalsContextCreateWorkflowReviewMutation$data,
	ApprovalsContextCreateWorkflowReviewMutation$variables,
} from './__generated__/ApprovalsContextCreateWorkflowReviewMutation.graphql.ts';
import type {
	ApprovalsContextUpdateReviewerDecisionMutation$data,
	ApprovalsContextUpdateReviewerDecisionMutation$variables,
	ConfluenceReviewerDecisionState,
} from './__generated__/ApprovalsContextUpdateReviewerDecisionMutation.graphql.ts';
import type {
	ApprovalsContextUpdateWorkflowReviewMutation$data,
	ApprovalsContextUpdateWorkflowReviewMutation$variables,
} from './__generated__/ApprovalsContextUpdateWorkflowReviewMutation.graphql.ts';
import type {
	ApprovalsContextWorkflowStatusesQuery$data,
	ApprovalsContextWorkflowStatusesQuery$variables,
} from './__generated__/ApprovalsContextWorkflowStatusesQuery.graphql.ts';
import type {
	ApprovalsContextActiveWorkflowApplicationQuery$data,
	ApprovalsContextActiveWorkflowApplicationQuery$variables,
	ConfluenceWorkflowReviewStatus,
	ConfluencePrincipalType as PrincipalType,
	ConfluenceReviewerDecisionState as ReviewerDecisionStatus,
} from './__generated__/ApprovalsContextActiveWorkflowApplicationQuery.graphql.ts';

// GraphQL Mutations - AGG Ready
// Mutation: Create Workflow Application
const createWorkflowApplicationMutation = graphql`
	mutation ApprovalsContextCreateWorkflowApplicationMutation(
		$cloudId: ID!
		$createWorkflowApplicationRequestInput: ConfluenceCreateWorkflowApplicationRequestInput!
	) {
		confluence_createWorkflowApplication(
			cloudId: $cloudId
			createWorkflowApplicationRequestInput: $createWorkflowApplicationRequestInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
			workflowStatus {
				id
				allowsReviews
				displayProperties {
					name
					description
					color
					icon
				}
			}
		}
	}
`;

// Mutation: Create Workflow Review
const createWorkflowReviewMutation = graphql`
	mutation ApprovalsContextCreateWorkflowReviewMutation(
		$cloudId: ID!
		$createContentApprovalRequestInput: ConfluenceCreateContentApprovalRequestInput!
	) {
		confluence_createContentApproval(
			cloudId: $cloudId
			createContentApprovalRequestInput: $createContentApprovalRequestInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
			contentApproval {
				id
				workflowApplicationId
				reviewStatus
				comment
				reviewers {
					principalType
					principalId
					isRequired
					user {
						id
						displayName
						photos {
							isPrimary
							value
						}
						emails {
							isPrimary
							value
						}
					}
				}
			}
		}
	}
`;

// Mutation: Update Reviewer Decision (Approve/Deny)
const updateReviewerDecisionMutation = graphql`
	mutation ApprovalsContextUpdateReviewerDecisionMutation(
		$cloudId: ID!
		$confluenceUpdateReviewerDecisionInput: ConfluenceUpdateReviewerDecisionInput!
	) {
		confluence_updateReviewerDecision(
			cloudId: $cloudId
			confluenceUpdateReviewerDecisionInput: $confluenceUpdateReviewerDecisionInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
		}
	}
`;

// Mutation: Update Workflow Review (add/remove reviewers, update status)
const updateWorkflowReviewMutation = graphql`
	mutation ApprovalsContextUpdateWorkflowReviewMutation(
		$cloudId: ID!
		$updateContentApprovalGraphQLInput: ConfluenceUpdateContentApprovalRequestInput!
	) {
		confluence_updateContentApproval(
			cloudId: $cloudId
			updateContentApprovalGraphQLInput: $updateContentApprovalGraphQLInput
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
			contentApproval {
				id
				reviewStatus
				comment
				reviewers {
					principalType
					principalId
					isRequired
					user {
						id
						displayName
						photos {
							isPrimary
							value
						}
						emails {
							isPrimary
							value
						}
					}
				}
			}
		}
	}
`;

// GraphQL Query - AGG Ready
// Query: Retrieve all available workflow statuses for given workflow id
const workflowStatusesQuery = graphql`
	query ApprovalsContextWorkflowStatusesQuery(
		$cloudId: ID!
		$transitionType: ConfluenceApprovalTransitionType
	) {
		confluence_contentApprovalStatuses(cloudId: $cloudId, transitionType: $transitionType) {
			id
			allowsReviews
			displayProperties {
				name
				color
			}
		}
	}
`;

// GraphQL Query - For fetching active workflow application
// Query: Retrieve Active Workflow Application for content
// This query gets the workflow application, which includes workflowReviewWithDecisions if available
const activeWorkflowApplicationQuery = graphql`
	query ApprovalsContextActiveWorkflowApplicationQuery(
		$cloudId: ID!
		$contentId: ID!
		$contentStatus: String!
		$contentVersion: Int
	) {
		content(id: $contentId) {
			nodes {
				status
				version {
					number
				}
			}
		}
		confluence_getActiveWorkflowApplication(
			cloudId: $cloudId
			contentId: $contentId
			contentStatus: $contentStatus
			contentVersion: $contentVersion
			workflowId: "1"
			fetchWorkflowReview: true
		) {
			success
			errors {
				message
				extensions {
					statusCode
					errorType
				}
			}
			workflowApplication {
				id
				workflowId
				contentId
				contentVersion
				contentStatus
				workflowStatusId
				isActive
				# lastModifiedAaid
				# lastModifiedDate {
				#	value
				# }
				# lastModifiedDate {
				#	value
				# }
				# user {
				#	id
				#	displayName
				#	photos {
				#		isPrimary
				#		value
				#	}
				# }
			}
			workflowReviewWithDecisions {
				id
				creatorAaid
				creationDate {
					value
				}
				reviewStatus
				reviewComment
				reviewLastModified {
					value
				}
				# user {
				#	id
				#	displayName
				#	photos {
				#		isPrimary
				#		value
				#	}
				# }
				reviewerDecisions {
					decisionId
					# reviewId
					# reviewerId
					required
					principalType
					principalId
					reviewerDecisionStatus
					comment
					# reviewLastModified {
					#	value
					# }
					user {
						id
						displayName
						photos {
							isPrimary
							value
						}
					}
				}
			}
		}
	}
`;

/**
 * Data structure for user information in content state updates.
 * Used by UserUpdateInfo component and ContentStateInfoPopup.
 * This is the canonical shape for user metadata in content state contexts.
 */
export type UserUpdateInfoData = {
	displayName: string;
	avatarUrl?: string;
	userId: string;
};

export type ApprovalStatus = ConfluenceWorkflowReviewStatus;

export type Approver = {
	decisionId: string;
	principalId: string;
	principalType: PrincipalType;
	name: string;
	avatarUrl?: string;
	email?: string;
	status: ConfluenceReviewerDecisionState;
	decisionComment?: string;
	isRequired: boolean;
	// respondedAt?: string;
};

/** Workflow application data from the active workflow application query */
export type ApprovalRequestApplication = {
	contentId: string;
	contentVersion: number;
	contentStatus: string;
	applicationId: string;
	isActive: boolean;
	workflowStatusId: string;
	// applicationLastModifiedBy?: UserUpdateInfoData;
	// applicationLastModifiedDate?: string;
};

/** Review data (requestor, review metadata, approvers). Null when there is an application but no review yet. */
export type ApprovalRequestReview = {
	// reviewId: string;
	// requestorId: string;
	// requestorName: string;
	// requestorAvatarUrl?: string;
	// reviewCreationDate: string;
	reviewLastModifiedDate: string;
	reviewComment?: string;
	reviewStatus: ApprovalStatus;
	approvers: Approver[];
};

export type ApprovalRequest = {
	application: ApprovalRequestApplication;
	review: ApprovalRequestReview | null;
};

export type WorkflowStatus = {
	id: string;
	name: string;
	color?: string;
	allowsReviews: boolean;
};

export type ApprovalsContextValue = {
	// Query data
	approvalRequest: ApprovalRequest | null;
	approvalRequestLoading: boolean;

	// Available workflow statuses
	availableWorkflowStatuses: WorkflowStatus[];
	workflowStatusesLoading: boolean;

	// Computed values
	isCurrentUserRequestor: boolean;
	isCurrentUserApprover: boolean;
	currentUserApprover: Approver | null;
	approvalProgress: { current: number; total: number };

	// UI state
	isPaneOpen: boolean;
	setIsPaneOpen: (isOpen: boolean) => void;
};

export type StartApprovalVariables = {
	contentId: string;
	approvers: Array<{
		principalId: string;
		principalType: PrincipalType;
		isRequired: boolean;
	}>;
	message?: string;
};

export type UpdateApprovalStatusVariables = {
	reviewerId: string;
	status: Extract<ReviewerDecisionStatus, 'REVIEWER_APPROVED' | 'REVIEWER_DENIED'>;
	comment?: string;
};

export type AddApproverVariables = {
	workflowApplicationId: string;
	contentApprovalReviewId: string;
	approvers: Array<{
		principalId: string;
		principalType: PrincipalType;
		isRequired: boolean;
	}>;
};

export type CancelApprovalVariables = {
	workflowApplicationId: string;
	contentApprovalReviewId: string;
};

export type ApprovalsDispatchContextValue = {
	// Mutations
	createWorkflowApplication: () => Promise<void>;
	startApproval: (variables: StartApprovalVariables) => Promise<void>;
	updateApprovalStatus: (variables: UpdateApprovalStatusVariables) => Promise<void>;
	addApprover: (variables: AddApproverVariables) => Promise<void>;
	cancelApproval: (variables: CancelApprovalVariables) => Promise<void>;
};

// Default context values
const defaultContextValue: ApprovalsContextValue = {
	approvalRequest: null,
	approvalRequestLoading: false,
	availableWorkflowStatuses: [],
	workflowStatusesLoading: false,
	isCurrentUserRequestor: false,
	isCurrentUserApprover: false,
	currentUserApprover: null,
	approvalProgress: { current: 0, total: 0 },
	isPaneOpen: false,
	setIsPaneOpen: () => {
		throw new Error('setIsPaneOpen must be used within ApprovalsProvider');
	},
};

const defaultDispatchValue: ApprovalsDispatchContextValue = {
	createWorkflowApplication: async () => {
		throw new Error('createWorkflowApplication not implemented');
	},
	startApproval: async () => {
		throw new Error('startApproval not implemented');
	},
	updateApprovalStatus: async () => {
		throw new Error('updateApprovalStatus not implemented');
	},
	addApprover: async () => {
		throw new Error('addApprover not implemented');
	},
	cancelApproval: async () => {
		throw new Error('cancelApproval not implemented');
	},
};

// Create contexts
export const ApprovalsContext = createContext<ApprovalsContextValue>(defaultContextValue);
ApprovalsContext.displayName = 'ApprovalsContext';

export const ApprovalsDispatchContext =
	createContext<ApprovalsDispatchContextValue>(defaultDispatchValue);
ApprovalsDispatchContext.displayName = 'ApprovalsDispatchContext';

// Provider props
type ApprovalsProviderProps = {
	children: ReactNode;
};

/**
 * ApprovalsProvider
 *
 * Provides approval data and mutation functions to child components.
 *
 * This provider:
 * 1. Fetches approval data using GraphQL query
 * 2. Exposes data through read context (ApprovalsContext)
 * 3. Exposes mutations through write context (ApprovalsDispatchContext)
 * 4. Handles cache updates and refetching after mutations
 *
 * @example
 * ```tsx
 * <ApprovalsProvider>
 *   <BylineApprovalStatus />
 *   <ApprovalsPane />
 * </ApprovalsProvider>
 * ```
 */
export const ApprovalsProvider: FC<ApprovalsProviderProps> = ({ children }) => {
	const { cloudId, userId } = useSessionData();
	const [contentIdFromHook] = usePageContentId();
	const contentId = contentIdFromHook ?? '';
	const pageMode = useGetPageMode();
	const contentStatus = pageMode === PageMode.EDIT ? 'DRAFT' : 'CURRENT';

	const [isPaneOpen, setIsPaneOpen] = React.useState(false);

	// Query for available workflow statuses - AGG Ready
	const { data: workflowStatusesData, loading: workflowStatusesLoading } = useQueryInterop<
		ApprovalsContextWorkflowStatusesQuery$data,
		ApprovalsContextWorkflowStatusesQuery$variables
	>(workflowStatusesQuery, {
		variables: {
			cloudId,
			transitionType: null, // null returns all statuses
		},
		skip: !cloudId || !expValEquals('confluence_approvals_eap', 'isEnabled', true),
	});

	// Transform workflow statuses data
	const availableWorkflowStatuses = useMemo(() => {
		if (!workflowStatusesData?.confluence_contentApprovalStatuses) return [];
		return workflowStatusesData.confluence_contentApprovalStatuses.reduce<WorkflowStatus[]>(
			(acc, status) => {
				if (status === null || status === undefined) return acc;
				acc.push({
					id: status.id || '',
					name: status.displayProperties?.name || '',
					allowsReviews: status.allowsReviews || false,
					color: status.displayProperties?.color || undefined,
				});
				return acc;
			},
			[],
		);
	}, [workflowStatusesData]);

	// Query for active workflow application
	// For this initial query, don't pass in contentVersion; it's not available for SSR,
	// and leaving it out will fetch the workflowApplication for the latest version of the content.
	const {
		data: activeWorkflowApplicationData,
		loading: activeWorkflowApplicationLoading,
		refetch: refetchActiveWorkflowApplication,
	} = useQueryInterop<
		ApprovalsContextActiveWorkflowApplicationQuery$data,
		ApprovalsContextActiveWorkflowApplicationQuery$variables
	>(activeWorkflowApplicationQuery, {
		variables: {
			cloudId,
			contentId,
			contentStatus,
		},
		skip: !cloudId || !contentId || !expValEquals('confluence_approvals_eap', 'isEnabled', true),
	});

	const contentVersion = useMemo(() => {
		const fromWorkflow =
			activeWorkflowApplicationData?.confluence_getActiveWorkflowApplication?.workflowApplication
				?.contentVersion;
		if (fromWorkflow !== undefined && fromWorkflow !== null) {
			return fromWorkflow;
		}
		return activeWorkflowApplicationData?.content?.nodes?.[0]?.version?.number ?? 0;
	}, [activeWorkflowApplicationData]);

	const approvalRequest = useMemo((): ApprovalRequest | null => {
		const application =
			activeWorkflowApplicationData?.confluence_getActiveWorkflowApplication?.workflowApplication;
		const review =
			activeWorkflowApplicationData?.confluence_getActiveWorkflowApplication
				?.workflowReviewWithDecisions;
		if (!application) return null;
		return {
			// TODO: Add commented out fields when they become available
			application: {
				contentId: application.contentId || contentId,
				contentVersion: application.contentVersion || 1,
				contentStatus: application.contentStatus || '', // NOTE: This indicates draft (DRAFT) or published (CURRENT)
				applicationId: application.id || '',
				isActive: application.isActive || false,
				workflowStatusId: application.workflowStatusId || '',
				// applicationLastModifiedBy: {
				// 	displayName: application.user?.displayName || '',
				// 	avatarUrl: application.user?.photos?.find((p) => p?.isPrimary)?.value || undefined,
				// 	userId: application.user?.id || '',
				// },
				// applicationLastModifiedDate: application?.lastModifiedDate?.value || '',
			},
			// There can be an application without a review; return review: null in that case
			review:
				review !== null && review !== undefined
					? {
							// requestorId: review.creatorAaid || '',
							// requestorName: review.user?.displayName || '',
							// requestorAvatarUrl:
							// 	review.user?.photos?.find((p) => p?.isPrimary)?.value || undefined,
							// reviewCreationDate: review.creationDate?.value ?? '', // milliseconds since epoch
							// reviewId: review.id || '',
							reviewLastModifiedDate: review.reviewLastModified?.value || '', // milliseconds since epoch
							reviewComment: review.reviewComment || undefined,
							reviewStatus: review.reviewStatus || 'PENDING',
							approvers: review.reviewerDecisions?.reduce<Approver[]>((acc, reviewerDecision) => {
								if (reviewerDecision == null) return acc;
								acc.push({
									decisionId: '',
									isRequired: true,
									principalId: reviewerDecision.principalId || '',
									principalType: reviewerDecision.principalType || 'USER',
									name: reviewerDecision.user?.displayName || '',
									avatarUrl: reviewerDecision.user?.photos?.find((p) => p?.isPrimary)?.value || '',
									status: reviewerDecision.reviewerDecisionStatus || 'PENDING_REVIEW',
									decisionComment: reviewerDecision.comment || '',
									// respondedAt: reviewerDecision.lastModifiedDate?.value,
								});
								return acc;
							}, []),
						}
					: null,
		};
	}, [
		activeWorkflowApplicationData?.confluence_getActiveWorkflowApplication?.workflowApplication,
		activeWorkflowApplicationData?.confluence_getActiveWorkflowApplication
			?.workflowReviewWithDecisions,
		contentId,
	]);

	// Computed values
	const isCurrentUserRequestor = useMemo(() => {
		// TODO: Add this when requestorId is available
		// return approvalRequest?.review?.requestorId === userId;
		return true;
	}, []);

	const currentUserApprover = useMemo(() => {
		return (
			approvalRequest?.review?.approvers?.find((approver) => approver.principalId === userId) ||
			null
		);
	}, [approvalRequest, userId]);

	const approvalProgress = useMemo(() => {
		if (!approvalRequest) return { current: 0, total: 0 };
		const responded = approvalRequest.review?.approvers?.filter(
			(a) => a.status !== null && a.status !== 'PENDING_REVIEW',
		).length;
		return {
			current: responded ?? 0,
			total: approvalRequest.review?.approvers?.length ?? 0,
		};
	}, [approvalRequest]);

	// GraphQL Mutations
	const [createWorkflowApplicationMutationFn] = useMutationInterop<
		ApprovalsContextCreateWorkflowApplicationMutation$data,
		ApprovalsContextCreateWorkflowApplicationMutation$variables
	>(createWorkflowApplicationMutation, {
		onCompleted: () => {
			void refetchActiveWorkflowApplication({
				cloudId,
				contentId,
				contentStatus,
				contentVersion,
			});
		},
	});

	const [createWorkflowReviewMutationFn] = useMutationInterop<
		ApprovalsContextCreateWorkflowReviewMutation$data,
		ApprovalsContextCreateWorkflowReviewMutation$variables
	>(createWorkflowReviewMutation, {
		onCompleted: () => {
			void refetchActiveWorkflowApplication({
				cloudId,
				contentId,
				contentStatus,
				contentVersion,
			});
		},
	});

	const [updateReviewerDecisionMutationFn] = useMutationInterop<
		ApprovalsContextUpdateReviewerDecisionMutation$data,
		ApprovalsContextUpdateReviewerDecisionMutation$variables
	>(updateReviewerDecisionMutation, {
		onCompleted: () => {
			void refetchActiveWorkflowApplication({
				cloudId,
				contentId,
				contentStatus,
				contentVersion,
			});
		},
	});

	const [updateWorkflowReviewMutationFn] = useMutationInterop<
		ApprovalsContextUpdateWorkflowReviewMutation$data,
		ApprovalsContextUpdateWorkflowReviewMutation$variables
	>(updateWorkflowReviewMutation, {
		onCompleted: () => {
			void refetchActiveWorkflowApplication({
				cloudId,
				contentId,
				contentStatus,
				contentVersion,
			});
		},
	});

	// const [updateWorkflowReviewStatusMutation] = useMutationInterop<
	// 	ApprovalsContextUpdateWorkflowReviewStatusMutation$data,
	// 	ApprovalsContextUpdateWorkflowReviewStatusMutation$variables
	// >(updateWorkflowReviewStatusMutation, {
	// 	refetchQueries: [
	// 		{
	// 			query: pageApprovalQuery,
	// 			variables: { contentId },
	// 		},
	// 	],
	// });

	// const [addApproverToWorkflowReviewMutation] = useMutationInterop<
	// 	ApprovalsContextAddApproverToWorkflowReviewMutation$data,
	// 	ApprovalsContextAddApproverToWorkflowReviewMutation$variables
	// >(addApproverToWorkflowReviewMutation, {
	// 	refetchQueries: [
	// 		{
	// 			query: pageApprovalQuery,
	// 			variables: { contentId },
	// 		},
	// 	],
	// });

	// const [removeApproverFromWorkflowReviewMutation] = useMutationInterop<
	// 	ApprovalsContextRemoveApproverFromWorkflowReviewMutation$data,
	// 	ApprovalsContextRemoveApproverFromWorkflowReviewMutation$variables
	// >(removeApproverFromWorkflowReviewMutation, {
	// 	refetchQueries: [
	// 		{
	// 			query: pageApprovalQuery,
	// 			variables: { contentId },
	// 		},
	// 	],
	// });

	// const [cancelWorkflowReviewMutation] = useMutationInterop<
	// 	ApprovalsContextCancelWorkflowReviewMutation$data,
	// 	ApprovalsContextCancelWorkflowReviewMutation$variables
	// >(cancelWorkflowReviewMutation, {
	// 	refetchQueries: [
	// 		{
	// 			query: pageApprovalQuery,
	// 			variables: { contentId },
	// 		},
	// 	],
	// });

	// Wrapped mutation functions
	const createWorkflowApplication = useCallback(async () => {
		if (!cloudId) {
			throw new Error('cloudId is required');
		}

		await createWorkflowApplicationMutationFn({
			variables: {
				cloudId,
				createWorkflowApplicationRequestInput: {
					contentId: parseInt(contentId, 10),
					contentStatus,
					version: contentStatus === 'CURRENT' ? contentVersion : 1, // DRAFT version is always 1
					workflowId: 1, // Approvals workflow ID is always 1
					workflowStatusId: 1, // In review has a fixed workflow status ID of 1
					isActive: true,
				},
			},
		});
	}, [cloudId, contentId, contentStatus, contentVersion, createWorkflowApplicationMutationFn]);

	const startApproval = useCallback(
		async (variables: Pick<StartApprovalVariables, 'message' | 'approvers'>) => {
			if (!cloudId) {
				throw new Error('cloudId is required');
			}

			await createWorkflowReviewMutationFn({
				variables: {
					cloudId,
					createContentApprovalRequestInput: {
						contentId: parseInt(contentId, 10),
						contentStatus,
						version: contentStatus === 'CURRENT' ? contentVersion : 1, // DRAFT version is always 1,
						comment: variables.message,
						reviewers: variables.approvers,
					},
				},
			});
		},
		[cloudId, contentId, contentStatus, contentVersion, createWorkflowReviewMutationFn],
	);

	const updateApprovalStatus = useCallback(
		async (variables: UpdateApprovalStatusVariables) => {
			if (!cloudId) {
				throw new Error('cloudId is required');
			}

			const decisionStatus = variables.status;

			await updateReviewerDecisionMutationFn({
				variables: {
					cloudId,
					confluenceUpdateReviewerDecisionInput: {
						reviewerId: parseInt(
							approvalRequest?.review?.approvers?.find(
								(reviewer) => reviewer.principalId === variables.reviewerId,
							)?.decisionId ?? '',
							10,
						),
						decisionStatus,
						comment: variables.comment || null,
					},
				},
			});
		},
		[approvalRequest?.review?.approvers, cloudId, updateReviewerDecisionMutationFn],
	);

	const addApprover = useCallback(
		async (variables: AddApproverVariables) => {
			if (!cloudId) {
				throw new Error('cloudId is required');
			}

			await updateWorkflowReviewMutationFn({
				variables: {
					cloudId,
					updateContentApprovalGraphQLInput: {
						workflowApplicationId: parseInt(approvalRequest?.application?.applicationId ?? '', 10),
						contentApprovalReviewId: 0, // parseInt(approvalRequest?.review?.reviewId ?? '', 10),
						reviewers: variables.approvers.map((approver) => ({
							principalId: approver.principalId,
							principalType: approver.principalType,
							isRequired: approver.isRequired,
						})),
					},
				},
			});
		},
		[approvalRequest?.application?.applicationId, cloudId, updateWorkflowReviewMutationFn],
	);

	const cancelApproval = useCallback(
		async (variables: CancelApprovalVariables) => {
			if (!cloudId) {
				throw new Error('cloudId is required');
			}

			await updateWorkflowReviewMutationFn({
				variables: {
					cloudId,
					updateContentApprovalGraphQLInput: {
						workflowApplicationId: parseInt(variables.workflowApplicationId, 10),
						contentApprovalReviewId: parseInt(variables.contentApprovalReviewId, 10),
						approvalStatus: 'CANCELED',
					},
				},
			});
		},
		[cloudId, updateWorkflowReviewMutationFn],
	);

	// Context values - memoized to prevent unnecessary re-renders
	const contextValue = useMemo<ApprovalsContextValue>(
		() => ({
			approvalRequest,
			approvalRequestLoading: activeWorkflowApplicationLoading,
			contentVersion,
			availableWorkflowStatuses,
			workflowStatusesLoading,
			isCurrentUserRequestor,
			isCurrentUserApprover: !!currentUserApprover,
			currentUserApprover,
			approvalProgress,
			isPaneOpen,
			setIsPaneOpen,
		}),
		[
			approvalRequest,
			activeWorkflowApplicationLoading,
			contentVersion,
			availableWorkflowStatuses,
			workflowStatusesLoading,
			isCurrentUserRequestor,
			currentUserApprover,
			approvalProgress,
			isPaneOpen,
		],
	);

	const dispatchValue = useMemo<ApprovalsDispatchContextValue>(
		() => ({
			createWorkflowApplication,
			startApproval,
			updateApprovalStatus,
			addApprover,
			cancelApproval,
		}),
		[createWorkflowApplication, startApproval, updateApprovalStatus, addApprover, cancelApproval],
	);

	return (
		<ApprovalsContext.Provider value={contextValue}>
			<ApprovalsDispatchContext.Provider value={dispatchValue}>
				{children}
			</ApprovalsDispatchContext.Provider>
		</ApprovalsContext.Provider>
	);
};

/**
 * Hook to access approval data (read-only)
 *
 * @example
 * ```tsx
 * const { approvalRequest, loading, approvalProgress } = useApprovals();
 * ```
 */
export const useApprovals = (): ApprovalsContextValue => {
	return useContext(ApprovalsContext);
};

/**
 * Hook to access approval mutations and actions (write-only)
 *
 * @example
 * ```tsx
 * const { startApproval, updateApprovalStatus } = useApprovalsDispatch();
 * ```
 */
export const useApprovalsDispatch = (): ApprovalsDispatchContextValue => {
	return useContext(ApprovalsDispatchContext);
};
